import { useCallback, useEffect, useRef } from "react";
import {
  ChatMessage,
  TEMP_REASONING_MESSAGE_ID,
  useMessagesStore,
} from "@store/messages.store";
import { useUIStore } from "@store/ui.store";
import { StreamDataMessage } from "@queries/chat/types/StreamDataMessage";
import { getAuthor } from "../chat-helpers";
import { useMessageStreamListener } from "@queries/chat/useMessageStreamListener";
import { User } from "@store/auth.store";
import { isValidChatMessageType } from "../components/Message/helpers";

const buildTempMessageId = (params: {
  threadId: string | undefined;
  promptRefId: string | null;
}) => {
  if (!params.threadId || !params.promptRefId) return null;
  return `resp_${params.threadId}:${params.promptRefId}`;
};

type UseMessageStreamHandlerProps = {
  currentThread: string | undefined;
  promptRefId: string | null;
  user: User;
  onStreamEnd: () => void;
  onStreamError: () => void;
};

type RunStepTypes =
  | "reasoning"
  | "tool_call"
  | "tool_calls"
  | "message_creation"
  | "completed";

const parseStreamData = (streamData: { data: object }): string => {
  return "content" in streamData.data &&
    typeof streamData.data.content === "string"
    ? streamData.data.content
    : "";
};

export const useMessageStreamHandler = ({
  currentThread,
  promptRefId,
  user,
  onStreamEnd,
  onStreamError,
}: UseMessageStreamHandlerProps) => {
  const upsertMessage = useMessagesStore((state) => state.upsertMessage);
  const addSubMessage = useMessagesStore((state) => state.addSubMessage);
  const updateStreamMessageText = useMessagesStore(
    (state) => state.updateStreamMessageText
  );
  const syncStreamMessage = useMessagesStore(
    (state) => state.syncStreamMessage
  );
  const messageInputText = useUIStore((state) => state.messageInputText);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const lastRunStepCreated = useRef<RunStepTypes | null>(null);

  useEffect(() => {
    if (messageInputText) messageInputRef.current = messageInputText;
  }, [messageInputText]);

  const handleStreamSuccess = useCallback(
    (args: { streamData: StreamDataMessage; threadId: string }) => {
      const { streamData, threadId } = args;
      const tempMessageId = buildTempMessageId({ threadId, promptRefId });
      const tempToolCallMessageWithImageOutputId =
        "resp_temp-tool-call-with-image-output-id";

      if (streamData.type === "end") {
        onStreamEnd();
        lastRunStepCreated.current = null;
        messageInputRef?.current?.focus();
        return;
      }

      if (streamData.type === "toolCallCompleted") {
        if (!isValidChatMessageType(streamData.data.type)) {
          console.error("Unkonwn message type:", streamData.data.type);
          return;
        }
        const newMessage = {
          id: streamData.data.uniqueKey ?? streamData.data.createdAt.toString(),
          text: parseStreamData(streamData.data),
          createdAt: new Date(streamData.data.createdAt),
          streaming: false,
          type: streamData.data.type,
          user: getAuthor({ createdBy: streamData.data.createBy, user }),
          createdBy: streamData.data.createBy,
        } as const;
        lastRunStepCreated.current = "completed";
        addSubMessage(threadId, tempMessageId || "", newMessage);
        return;
      }

      if (streamData.type === "runStepCompleted") {
        if (
          streamData.data.type === "tool_calls" &&
          streamData.data.hasCodeInterpreterImageOutput
        ) {
          const newMessage = {
            id: tempToolCallMessageWithImageOutputId,
            text: "",
            createdAt: new Date(),
            streaming: false,
            type: "image-fallback",
            user: getAuthor({ createdBy: "bot", user: null }),
            createdBy: "bot",
          } as const;
          addSubMessage(threadId, tempMessageId || "", newMessage);
        }
        lastRunStepCreated.current = "completed";
      }

      if (!tempMessageId) return;

      if (streamData.type === "runCreated") {
        if (lastRunStepCreated.current === null) {
          // first runStep
          const newMessage = {
            id: tempMessageId,
            text: "",
            createdAt: new Date(),
            streaming: true,
            type: "text",
            user: getAuthor({ createdBy: "bot", user: null }),
            createdBy: "bot",
          } as const satisfies ChatMessage;
          upsertMessage(threadId, newMessage);
        }
        return;
      }

      if (streamData.type === "runStepCreated") {
        lastRunStepCreated.current = streamData.data.type;
        const baseBotMessage = {
          createdAt: new Date(),
          user: getAuthor({ createdBy: "bot", user: null }),
          createdBy: "bot",
        };
        if (streamData.data.type === "message_creation") {
          upsertMessage(threadId, {
            ...baseBotMessage,
            id: tempMessageId,
            streaming: false,
          });
        }
        if (streamData.data.type === "reasoning") {
          const emptySubMessage = {
            ...baseBotMessage,
            id: TEMP_REASONING_MESSAGE_ID,
            text: "",
            streaming: true,
            type: "text",
          } satisfies NonNullable<ChatMessage["subMessages"]>[number];
          addSubMessage(threadId, tempMessageId, emptySubMessage);
        }
        return;
      }

      if (streamData.type === "messageTextDelta") {
        if (lastRunStepCreated.current === "message_creation") {
          updateStreamMessageText(threadId, tempMessageId, streamData.data);
        }
        if (lastRunStepCreated.current === "reasoning") {
          const emptySubMessage = {
            id: TEMP_REASONING_MESSAGE_ID,
            text: streamData.data,
            createdAt: new Date(),
            streaming: true,
            type: "text",
            user: getAuthor({ createdBy: "bot", user: null }),
            createdBy: "bot",
          } satisfies NonNullable<ChatMessage["subMessages"]>[number];
          addSubMessage(threadId, tempMessageId, emptySubMessage);
        }
        return;
      }

      if (streamData.type === "messageCompleted") {
        const { metadata, data } = streamData;
        data.forEach((message, idx) => {
          if (!isValidChatMessageType(message.type)) {
            console.error("Unkonwn message type:", message.type);
            return;
          }
          const finalMessage = {
            id: message.uniqueKey,
            text: parseStreamData(message),
            createdAt: new Date(message.createdAt),
            streaming: false,
            type: message.type,
            user: getAuthor({ createdBy: message.createBy, user }),
            createdBy: message.createBy,
          };
          const isReasoningMessage =
            message.uniqueKey.startsWith("rs_") || message.type === "reasoning";

          if (isReasoningMessage) {
            addSubMessage(threadId, tempMessageId, finalMessage);
            return;
          }
          if (!isReasoningMessage) {
            if (idx === 0) {
              syncStreamMessage(
                threadId,
                tempMessageId,
                finalMessage,
                metadata
              );
              return;
            }

            if (idx === 1) {
              syncStreamMessage(
                threadId,
                tempToolCallMessageWithImageOutputId,
                finalMessage,
                metadata
              );
              return;
            }
          }
        });
        return;
      }
    },
    [
      addSubMessage,
      promptRefId,
      onStreamEnd,
      syncStreamMessage,
      updateStreamMessageText,
      upsertMessage,
      user,
    ]
  );

  const handleStreamError = useCallback(() => {
    onStreamError();
    lastRunStepCreated.current = null;
  }, [onStreamError]);

  const messageStreamListener = useMessageStreamListener({
    promptRefId,
    threadId: currentThread,
    onSuccess: handleStreamSuccess,
    onError: handleStreamError,
  });

  return messageStreamListener;
};
